import React, { ReactNode, useState, useMemo, useEffect, useRef } from 'react'
import { Badge, Col, Row, Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toTitleCase } from '../../../utils/helpers'
import { useFetchUserProfileQuery } from '../../../services/authApi'
import { useLocation, useNavigate } from 'react-router-dom'
import SelectDatasetModal from '../../../components/modals/SelectDatasetModal'
import UploadWizard from '../../../components/wizards/UploadWizard'
import ExportModal from '../../../components/modals/ExportModal'
import NewModelModal from '../../../components/modals/NewModelModal'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectDatasetSources, setDatasetSources } from '../../../store/slices/datasetSlice'
import {
  selectSelectedModelId,
  setSelectedModelId,
  handleProjectChange,
} from '../../../store/slices/modelSlice'
import {
  useCreateModelMutation,
  useGetModelQuery,
  useFetchModelsQuery,
} from '../../../services/modelsApi'
import {
  CloudUpload,
  Database,
  Download,
  Plus,
  Image,
  Tag,
  Box,
  Activity,
  Rocket,
  ChevronRight,
  BarChart2,
  MessageCircle,
} from 'lucide-react'
import PillsDataset from '../../../components/PillsDataset'
import styled from 'styled-components'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { ModelCreateParams } from '../../../types/api/api-model'
import { adjectives, nouns } from '../../../utils/modelNameWords'
import ErrorDialog from '../../modals/ErrorDialog'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { useDatasetStats } from '../../../utils/datasetStats'
import { useGetTagsQuery } from '../../../services/tagApi'
import SelectModelTreeModal from '../../modals/SelectModelTreeModal'
import SelectProjectModal from '../../modals/SelectProjectModal'
import { SynthDataModal } from '../../modals/SynthDataModal/SynthDataModal'
import { AIChatModal } from '../../modals/AIChatModal'
import { Model } from '../../../types/models/model'

interface HeaderRowProps {
  project?: {
    name?: string
    annotation_type?: string
  }
  pageHeaderText: {
    title: string
    subtitle: string
  }
  controls?: ReactNode
}

const HeaderCard = styled.div`
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease;
  background: rgba(33, 37, 41, 0.95);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border-color: var(--bs-primary);
    background: rgba(40, 44, 48, 0.95);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  }

  .header-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 0;
  }

  .header-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .content {
    display: flex;
    flex-direction: row;
    gap: 6px;
    align-items: center;
  }

  h3 {
    color: white;
    font-size: 1.6rem;
    margin: 0;
  }
`

const DatasetSelector = styled(HeaderCard)`
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 0;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    border: none;
    box-shadow: none;
    background: rgba(40, 44, 48, 0.5);
  }

  .dataset-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 0.2rem;
  }

  .pills-container {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 0.5rem;
  }

  .more-datasets {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--bs-primary);
    font-size: 0.875rem;
    padding: 2px 8px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.1);
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: center;
  margin-top: auto;

  .btn {
    flex: 1;
    padding: 0.5rem;
    border-radius: 8px;
    font-weight: 500;
    letter-spacing: 0.3px;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    &:active {
      transform: translateY(1px);
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }
  }

  .purple-btn {
    background: rgba(138, 43, 226, 0.1) !important;
    border-color: rgba(138, 43, 226, 0.5) !important;
    color: rgba(200, 150, 255, 1) !important;

    &:hover {
      background: rgba(138, 43, 226, 0.2) !important;
      border-color: rgba(138, 43, 226, 0.7) !important;
      color: rgba(220, 190, 255, 1) !important;
    }
  }

  .btn-outline-primary {
    background: rgba(138, 43, 226, 0.1);
    border-color: rgba(138, 43, 226, 0.5);
    color: rgba(200, 150, 255, 1);

    &:hover {
      background: rgba(138, 43, 226, 0.2);
      border-color: rgba(138, 43, 226, 0.7);
      color: rgba(220, 190, 255, 1);
    }
  }

  .btn-outline-success {
    background: rgba(25, 135, 84, 0.1);
    border-color: rgba(25, 135, 84, 0.5);

    &:hover {
      background: rgba(25, 135, 84, 0.2);
      border-color: rgba(25, 135, 84, 0.7);
    }
  }

  .btn-outline-info {
    background: rgba(13, 202, 240, 0.1);
    border-color: rgba(13, 202, 240, 0.5);

    &:hover {
      background: rgba(13, 202, 240, 0.2);
      border-color: rgba(13, 202, 240, 0.7);
    }
  }
`

// Update styled components
const StatsRow = styled(Row)`
  position: relative;
  z-index: 1000;
  margin-top: 1.2rem;
  padding: 0;
  margin-left: 0;
  margin-right: 0;
  width: 100%;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 36px;
`

const HeaderContainer = styled.div`
  position: relative;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
`

const RouteHeader = styled.h3`
  color: white;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
  padding: 0;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 1rem;
  white-space: nowrap;
  line-height: 1;
`

const MainSection = styled.span`
  background: linear-gradient(135deg, #fff 0%, #e0e0e0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

const SubSection = styled.span`
  color: var(--bs-primary);
  opacity: 0.9;
  font-weight: 500;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 2px;
    background: var(--bs-primary);
    transform: scaleX(0);
    transition: transform 0.3s ease;
    transform-origin: left;
  }

  &:hover::before {
    transform: scaleX(1);
  }
`

const HeaderSeparator = styled(ChevronRight)`
  width: 24px;
  height: 24px;
  color: #666;
  opacity: 0.7;
`

const StatItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  margin: 0;
  border-right: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.2s ease;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  height: 32px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &:last-child {
    border-right: none;
  }

  .icon {
    color: rgba(255, 255, 255, 0.5);
    width: 16px;
    height: 16px;
  }

  .stat-content {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .stat-value {
    font-size: 0.95rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    line-height: 1;
  }

  .stat-label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.5);
    margin: 0;
    line-height: 1;
  }
`

// Add new styled component for the model display
const ModelDisplay = styled.div`
  background: rgba(var(--bs-primary-rgb), 0.1);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  border-radius: 6px;
  padding: 0.25rem 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(var(--bs-primary-rgb), 0.15);
    border-color: rgba(var(--bs-primary-rgb), 0.3);
  }

  .model-name {
    color: var(--bs-primary);
    font-weight: 500;
    display: block;
    font-size: 1.1rem;
    margin-top: 0;
    padding-top: 2px;
  }

  .no-model {
    color: rgba(255, 255, 255, 0.5);
    font-style: italic;
    font-size: 1.1rem;
    margin-top: 0;
    padding-top: 2px;
  }
`

// Add new styled component for the project display
const ProjectDisplay = styled.div`
  background: rgba(var(--bs-primary-rgb), 0.1);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(var(--bs-primary-rgb), 0.15);
    border-color: rgba(var(--bs-primary-rgb), 0.3);
  }

  h3 {
    background: linear-gradient(135deg, #fff 0%, var(--bs-primary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
    letter-spacing: 0.3px;
  }

  .welcome-text {
    background: linear-gradient(135deg, #fff 0%, var(--bs-primary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 1.2rem;
    font-weight: 500;
  }
`

const ProjectLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 0.2rem;

  span:first-child {
    color: rgba(255, 255, 255, 0.7);
  }

  span:nth-child(2) {
    color: rgba(255, 255, 255, 0.5);
  }

  .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    font-weight: 500;
  }
`

const StatsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(40, 44, 48, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  &:hover {
    background: rgba(50, 54, 58, 0.8);
    transform: translateX(-50%) scale(1.05);
  }

  svg {
    color: rgb(255, 81, 0);
    filter: drop-shadow(0 0 3px rgba(0, 229, 255, 0.5));
  }
`

const AIChatIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(85, 60, 154, 0.2);
  border: 1px solid rgba(85, 60, 154, 0.4);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    background: rgba(85, 60, 154, 0.3);
    border-color: rgba(85, 60, 154, 0.6);
    transform: translateY(-50%) scale(1.05);
  }

  svg {
    color: #553c9a;
    filter: drop-shadow(0 0 3px rgba(85, 60, 154, 0.5));
  }
`

const BetaTag = styled.div`
  position: absolute;
  top: -8px;
  right: -12px;
  background: #553c9a;
  color: white;
  font-size: 8px;
  font-weight: bold;
  padding: 1px 4px;
  border-radius: 3px;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
`

// Update the HeaderRowContainer import to also be styled here
const HeaderRowContainer = styled.div`
  width: 100%;
  position: relative;
  z-index: 1001;
  background-color: #1a1a1a;
  display: flex;
  justify-content: center;
  padding: 0.75rem;
  align-items: center;
`

export const HeaderRow: React.FC<HeaderRowProps> = ({ project, controls }) => {
  const dispatch = useAppDispatch()
  const { data: user } = useFetchUserProfileQuery()
  const location = useLocation()
  const navigate = useNavigate()
  const [showDatasetModal, setShowDatasetModal] = useState(false)
  const [showUploadWizard, setShowUploadWizard] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showNewModelModal, setShowNewModelModal] = useState(false)
  const [showSelectModelModal, setShowSelectModelModal] = useState(false)
  const [showSelectProjectModal, setShowSelectProjectModal] = useState(false)
  const [showSynthModal, setShowSynthModal] = useState(false)
  const [showAIChatModal, setShowAIChatModal] = useState(false)
  const [filterText, setFilterText] = useState('')
  const [selectedBaseModel, setSelectedBaseModel] = useState<{
    id: string
    name: string
  } | null>(null)
  const selectedDatasets = useAppSelector(selectDatasetSources)
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const { data: selectedModel } = useGetModelQuery(selectedModelId ?? '', {
    skip: !selectedModelId,
  })
  const isModelsView = location.pathname.split('/')[1] === 'models'
  const isDatasetsView = location.pathname.split('/')[1] === 'datasets'
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [showError, setShowError] = useState(false)
  const [createModel] = useCreateModelMutation()
  const { data: projectData } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: tagsData } = useGetTagsQuery(
    {
      project_id: projectData?.id || '',
      dataset_id: projectData?.datasetid || '',
    },
    {
      skip: !selectedProjectId || !projectData?.datasetid,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )
  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    {
      skip: !selectedProjectId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )
  const headerDatasetStats = useDatasetStats(
    selectedProjectId || '',
    projectData?.datasetid || '',
    []
  )
  const headerAggregate = headerDatasetStats.__aggregate__

  // Calculate project statistics
  const projectStats = useMemo(() => {
    return {
      totalImages: projectData?.count || 0,
      totalAnnotations: headerAggregate?.total_annotated_images || 0,
      totalClasses: tagsData?.tags?.length || 0,
      totalModels: modelsData?.models?.length || 0,
      deployedModels: modelsData?.models?.filter(
        (model: Model) => model.deployment.status.toLowerCase() === 'in_service'
      ),
    }
  }, [projectData, headerAggregate?.total_annotated_images, tagsData, modelsData])

  // Add state for showing stats
  const [showStats, setShowStats] = useState(true)
  const statsTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Add a state to track if stats were manually opened
  const [manuallyOpened, setManuallyOpened] = useState(false)

  useEffect(() => {
    setShowSelectProjectModal(false)
  }, [selectedProjectId])

  // Handle project changes to update selected model
  useEffect(() => {
    if (selectedProjectId) {
      dispatch(handleProjectChange(selectedProjectId))
    }
  }, [selectedProjectId, dispatch])

  // Auto-hide stats after 5/10 seconds
  useEffect(() => {
    if (statsTimeoutRef.current) {
      clearTimeout(statsTimeoutRef.current)
    }

    // Set new timeout when stats are shown
    if (showStats) {
      // Use 10 seconds if manually opened, 5 seconds for route changes
      const timeoutDuration = manuallyOpened ? 10000 : 5000

      statsTimeoutRef.current = setTimeout(() => {
        setShowStats(false)
        setManuallyOpened(false) // Reset the manual flag when closing
      }, timeoutDuration)
    }

    return () => {
      if (statsTimeoutRef.current) {
        clearTimeout(statsTimeoutRef.current)
      }
    }
  }, [showStats, manuallyOpened, location.pathname])

  // Set initial stats display on route change
  useEffect(() => {
    setShowStats(true)
    setManuallyOpened(false) // Route change is not manual

    return () => {
      if (statsTimeoutRef.current) {
        clearTimeout(statsTimeoutRef.current)
      }
    }
  }, [location.pathname])

  const handleCreateModel = () => {
    setShowNewModelModal(true)
  }

  const generateModelName = () => {
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]
    return `${randomAdjective}-${randomNoun}`
  }

  const handleGenerateNewModel = async () => {
    if (project && selectedBaseModel) {
      try {
        const newModelName = generateModelName()
        const newModel = await createModel({
          project_id: selectedProjectId,
          name: newModelName,
          description: '',
        } as ModelCreateParams).unwrap()

        // Wait for a brief moment to allow the cache to update
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Set the selected model ID
        dispatch(
          setSelectedModelId({
            modelId: newModel.id,
            projectId: selectedProjectId || '',
          })
        )

        // Navigate to the training subview
        navigate(`/models/${selectedProjectId}/training`)

        // Close the modal
        setShowNewModelModal(false)
      } catch (err: unknown) {
        const message =
          (err as { data?: { message?: string } })?.data?.message || 'Failed to create new model'
        setErrorMessage(message)
        setShowError(true)
      }
    }
  }

  // Mock base models data - you might want to fetch this from an API in a real application
  const baseModels = [
    {
      id: 'base-1',
      name: 'AG Vision Base Model',
      image: '/DALLE-AI-Model-Logo-1.jpg',
    },
  ]

  const renderDatasetPills = () => {
    if (!selectedDatasets?.length) {
      return <PillsDataset tiny datasetNames={['All']} disabled={true} />
    }

    const visibleDatasets = selectedDatasets.slice(0, 2)
    const remainingCount = selectedDatasets.length - 2

    return (
      <div className="pills-container">
        {visibleDatasets.map((dataset: string, index: number) => (
          <PillsDataset key={index} tiny datasetNames={[dataset]} disabled={true} />
        ))}
        {remainingCount > 0 && (
          <span className="more-datasets">
            <span>+{remainingCount}</span>
          </span>
        )}
      </div>
    )
  }

  return (
    <HeaderRowContainer>
      <HeaderContainer>
        <Row className="w-100 g-3 px-2">
          {/* Left Column - Project Info */}
          <Col md={3} className="d-flex align-items-stretch">
            <HeaderCard className="w-100" onClick={() => setShowSelectProjectModal(true)}>
              <ProjectLabel>
                <span>Current Project</span>
                {location.pathname.split('/')[1] !== 'projects' && project?.annotation_type && (
                  <Badge
                    bg={
                      project.annotation_type === 'classification'
                        ? 'primary'
                        : project.annotation_type === 'oriented_bounding_box'
                        ? 'primary'
                        : 'success'
                    }
                  >
                    {toTitleCase(project.annotation_type)}
                  </Badge>
                )}
              </ProjectLabel>
              <ProjectDisplay>
                {location.pathname.split('/')[1] !== 'projects' ? (
                  <h3>{project?.name || ''}</h3>
                ) : (
                  <span className="welcome-text">Hi, {user?.first_name || ''}</span>
                )}
              </ProjectDisplay>
            </HeaderCard>
          </Col>

          {/* Middle Column - Dataset Selection and Tools */}
          <Col md={3} className="d-flex align-items-stretch">
            <HeaderCard className="w-100">
              <div className="w-100 h-100">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Click to select datasets</Tooltip>}
                >
                  <DatasetSelector onClick={() => setShowDatasetModal(true)}>
                    <div className="dataset-label">
                      <Database size={16} />
                      <span>Active Dataset(s)</span>
                    </div>
                    <div className="pills-container">{renderDatasetPills()}</div>
                  </DatasetSelector>
                </OverlayTrigger>
                {controls && <div className="mt-2">{controls}</div>}
              </div>
            </HeaderCard>
          </Col>

          {/* Model Selection Column */}
          <Col md={3} className="d-flex align-items-stretch">
            <HeaderCard className="w-100">
              <div className="w-100 h-100">
                <OverlayTrigger placement="top" overlay={<Tooltip>Click to select model</Tooltip>}>
                  <DatasetSelector onClick={() => setShowSelectModelModal(true)}>
                    <div className="dataset-label">
                      <Database size={16} />
                      <span>Selected Model</span>
                    </div>
                    <ModelDisplay>
                      {selectedModelId && selectedModel ? (
                        <span className="model-name">{selectedModel.name}</span>
                      ) : (
                        <span className="no-model">No model selected</span>
                      )}
                    </ModelDisplay>
                  </DatasetSelector>
                </OverlayTrigger>
              </div>
            </HeaderCard>
          </Col>

          {/* Right Column - Action Buttons */}
          <Col md={3} className="d-flex align-items-stretch">
            <HeaderCard className="w-100">
              <div className="header-label">Actions</div>
              <ActionButtons>
                {isModelsView ? (
                  <Button
                    variant="outline-primary"
                    onClick={handleCreateModel}
                    className="d-flex align-items-center justify-content-center purple-btn"
                  >
                    <Plus size={18} className="me-2" />
                    <span>New Model</span>
                  </Button>
                ) : isDatasetsView ? (
                  <>
                    <Button
                      variant="outline-success"
                      onClick={() => setShowUploadWizard(true)}
                      className="d-flex align-items-center justify-content-center"
                    >
                      <CloudUpload size={18} className="me-2" />
                      <span>Upload</span>
                    </Button>
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowSynthModal(true)}
                      className="d-flex align-items-center justify-content-center purple-btn"
                    >
                      <Database size={18} className="me-2" />
                      <span>Synth</span>
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline-success"
                    onClick={() => setShowUploadWizard(true)}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <CloudUpload size={18} className="me-2" />
                    <span>Upload</span>
                  </Button>
                )}
                <Button
                  variant="outline-info"
                  onClick={() => setShowExportModal(true)}
                  className="d-flex align-items-center justify-content-center"
                >
                  <Download size={18} className="me-2" />
                  <span>Export</span>
                </Button>
              </ActionButtons>
            </HeaderCard>
          </Col>
        </Row>

        {/* Stats Row with Route Header */}
        <StatsRow>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              paddingLeft: '15px',
              paddingRight: '15px',
              position: 'relative',
            }}
          >
            {/* Absolutely positioned far left for breadcrumbs */}
            <div
              style={{
                position: 'absolute',
                left: '15px',
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <RouteHeader>
                {(() => {
                  const segments = location.pathname.split('/').filter(Boolean)
                  if (segments.length > 0) {
                    const mainSection = segments[0].charAt(0).toUpperCase() + segments[0].slice(1)
                    const lastSegment = segments[segments.length - 1]
                    // Skip if the last segment looks like an ID (has numbers or is very long)
                    let finalSegment = /^[0-9a-f]{24}$/i.test(lastSegment)
                      ? null
                      : lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1)

                    // Special handling for adaptive-active-labeling route
                    if (lastSegment === 'adaptive-active-labeling') {
                      finalSegment = 'AAL'
                    }

                    return (
                      <>
                        <MainSection>{mainSection}</MainSection>
                        {finalSegment && finalSegment !== mainSection && (
                          <>
                            <HeaderSeparator />
                            <SubSection>{finalSegment}</SubSection>
                          </>
                        )}
                      </>
                    )
                  }
                  return ''
                })()}
              </RouteHeader>
            </div>

            {/* Centered stats section */}
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {showStats ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    gap: '0.75rem',
                    paddingTop: '2px',
                  }}
                  onClick={() => setShowStats(false)}
                >
                  <StatItem>
                    <Image size={18} className="icon" />
                    <div className="stat-content">
                      <p className="stat-value">{projectStats.totalImages}</p>
                      <p className="stat-label">Images</p>
                    </div>
                  </StatItem>
                  <StatItem>
                    <Tag size={18} className="icon" />
                    <div className="stat-content">
                      <p className="stat-value">{projectStats.totalAnnotations}</p>
                      <p className="stat-label">Annotations</p>
                    </div>
                  </StatItem>
                  <StatItem>
                    <Box size={18} className="icon" />
                    <div className="stat-content">
                      <p className="stat-value">{projectStats.totalClasses}</p>
                      <p className="stat-label">Classes</p>
                    </div>
                  </StatItem>
                  <StatItem>
                    <Activity size={18} className="icon" />
                    <div className="stat-content">
                      <p className="stat-value">{projectStats.totalModels}</p>
                      <p className="stat-label">Models</p>
                    </div>
                  </StatItem>
                  <StatItem>
                    <Rocket size={18} className="icon" />
                    <div className="stat-content">
                      <p className="stat-value">{projectStats.deployedModels?.length || '0'}</p>
                      <p className="stat-label">
                        Deployment
                        {projectStats.deployedModels?.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </StatItem>
                </div>
              ) : (
                <StatsIcon
                  onClick={() => {
                    setShowStats(true)
                    setManuallyOpened(true)
                  }}
                >
                  <BarChart2 size={18} />
                </StatsIcon>
              )}
            </div>

            {/* AI Chat Icon - positioned absolutely on the far right */}
            <AIChatIcon onClick={() => setShowAIChatModal(true)}>
              <MessageCircle size={18} />
              <BetaTag>BETA</BetaTag>
            </AIChatIcon>
          </div>
        </StatsRow>

        <SelectDatasetModal
          show={showDatasetModal}
          onHide={() => setShowDatasetModal(false)}
          handleAcceptSelections={(sources) => {
            dispatch(setDatasetSources(sources))
            setShowDatasetModal(false)
          }}
          selectedSources={selectedDatasets}
          canCreate={true}
        />

        <SelectModelTreeModal
          show={showSelectModelModal}
          onHide={() => setShowSelectModelModal(false)}
          onModelSelect={(model) => {
            dispatch(
              setSelectedModelId({
                modelId: model.id,
                projectId: selectedProjectId || '',
              })
            )
            setShowSelectModelModal(false)
          }}
          selectedModelId={selectedModelId ?? null}
        />

        <SelectProjectModal
          show={showSelectProjectModal}
          onHide={() => setShowSelectProjectModal(false)}
        />

        <UploadWizard show={showUploadWizard} onHide={() => setShowUploadWizard(false)} />

        <ExportModal show={showExportModal} onHide={() => setShowExportModal(false)} />

        <SynthDataModal show={showSynthModal} onHide={() => setShowSynthModal(false)} />

        <AIChatModal show={showAIChatModal} onHide={() => setShowAIChatModal(false)} />

        <NewModelModal
          show={showNewModelModal}
          onClose={() => {
            setShowNewModelModal(false)
            setSelectedBaseModel(null)
            setFilterText('')
          }}
          baseModels={baseModels}
          filterText={filterText}
          setFilterText={setFilterText}
          selectedBaseModel={selectedBaseModel}
          onBaseModelSelect={setSelectedBaseModel}
          onGenerateNewModel={handleGenerateNewModel}
        />
        <ErrorDialog
          show={showError}
          onHide={() => setShowError(false)}
          message={errorMessage}
          title="Error Creating Model"
        />
      </HeaderContainer>
    </HeaderRowContainer>
  )
}
